import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

export default function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const location = useLocation();

  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdmin) return;

    const handleScroll = () => {
      const scrolled = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      if (docHeight > 0) {
        const progress = (scrolled / docHeight) * 100;
        setScrollProgress(Math.min(progress, 100));
      }

      setIsVisible(scrolled > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAdmin, location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  if (isAdmin) return null;

  // SVG parameters
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  return (
    <div
      className={`fixed bottom-8 sm:bottom-10 lg:bottom-12  right-6 z-[9990] transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] transform ${
        isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-75 pointer-events-none'
      }`}
    >
      {/* Dynamic Cyber Glimmer/Pulse Background Ring */}
      <div 
        className={`absolute -inset-1.5 rounded-full bg-gradient-to-r from-cyber-pink via-accent to-cyber-blue opacity-30 blur-sm transition-all duration-700 ${
          isHovered ? 'animate-spin-slow opacity-90 blur-md scale-110' : 'animate-pulse-slow'
        }`}
      />

      <button
        onClick={scrollToTop}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#080810]/90 border border-white/10 backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(0,0,0,0.8)] cursor-pointer overflow-hidden group"
        aria-label="Scroll to top"
      >
        {/* Futuristic Cyber Scanline/Overlay HUD Effect */}
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_95%,rgba(0,240,255,0.15)_95%)] bg-[length:100%_8px] animate-[gridMove_8s_linear_infinite] opacity-40 pointer-events-none" />

        {/* Hover Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-cyber-pink/10 to-cyber-blue/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        {/* Central HUD crosshairs (Static futuristic details) */}
        <div className="absolute inset-2 rounded-full border border-white/5 pointer-events-none" />
        {/* <div className="absolute w-[2px] h-[6px] bg-cyber-blue/30 top-1 left-1/2 -translate-x-1/2" />
        <div className="absolute w-[2px] h-[6px] bg-cyber-blue/30 bottom-1 left-1/2 -translate-x-1/2" />
        <div className="absolute h-[2px] w-[6px] bg-cyber-blue/30 left-1 top-1/2 -translate-y-1/2" />
        <div className="absolute h-[2px] w-[6px] bg-cyber-blue/30 right-1 top-1/2 -translate-y-1/2" /> */}

        {/* SVG Progress Circle */}
        <svg className="absolute top-0 left-0 w-full h-full -rotate-90" viewBox="0 0 64 64">
          <defs>
            <linearGradient id="cyberGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00f0ff" />
              <stop offset="50%" stopColor="#bd00ff" />
              <stop offset="100%" stopColor="#ff007a" />
            </linearGradient>
          </defs>

          {/* Background Track */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            className="stroke-white/10 fill-none"
            strokeWidth="2.5"
          />

          {/* Progress Arc */}
          <circle
            cx="32"
            cy="32"
            r={radius}
            stroke="url(#cyberGradient)"
            className="fill-none transition-[stroke-dashoffset] duration-150 ease-out"
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>

        {/* Active Animated Arrow / Chevron Icon */}
        <div className="relative z-10 flex flex-col items-center justify-center text-cyber-blue group-hover:text-cyber-pink transition-colors duration-300">
          <ChevronUp 
            size={24} 
            className="transform transition-transform duration-300 ease-out group-hover:-translate-y-1.5 animate-[bounce_2s_infinite]" 
          />
          {/* Cyberpunk UI Text */}
          {/* <span className="text-[8px] font-cyber tracking-widest font-extrabold -mt-1 opacity-60 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300 text-cyber-cyan group-hover:text-white drop-shadow-[0_0_5px_rgba(0,240,255,0.5)]">
            SYS:UP
          </span> */}
        </div>

        {/* Outer Tech Brackets on Hover */}
        <div className="absolute inset-0.5 rounded-full border border-cyber-cyan/0 group-hover:border-cyber-cyan/20 scale-90 group-hover:scale-100 transition-all duration-500 pointer-events-none" />
      </button>
    </div>
  );
}
